import type {
  DocumentSectionSpec,
  DocumentTemplate,
  TemplateDocumentExport,
} from '@/entities/templates';

import { dropNoteLinksTo } from './blockTemplate';
import {
  describeNoteLine,
  type NoteBlockDef,
  type NoteLineDef,
} from './noteView';
import type { TemplateDraft } from './templateDraft';
import {
  describeSectionUpdate,
  type TemplateChangeSummary,
} from './templateEdit';

// The template the document reads as: its sections, with the links that
// named a removed block dropped.
export const applyTemplateDocument = (
  template: DocumentTemplate,
  { sections }: TemplateDocumentExport,
): DocumentTemplate => {
  const draft: TemplateDraft = {
    template: { ...template, sections },
    answers: {},
    aiAnswers: {},
  };
  const blockIds = new Set(
    sections.flatMap((section) =>
      section.Lines.flatMap((line) => line.Blocks.map((block) => block.ID)),
    ),
  );
  const missing = [
    ...new Set(
      sections.flatMap((section) =>
        section.Lines.flatMap((line) =>
          line.ShowWhen.map((condition) => condition.BlockID),
        ),
      ),
    ),
  ].filter((blockId) => !blockIds.has(blockId));
  return (missing.length ? dropNoteLinksTo(draft, missing) : draft).template;
};

const isSameStructure = (first: unknown, second: unknown) =>
  JSON.stringify(first) === JSON.stringify(second);

const longest = (chains: number[][]) =>
  chains.reduce(
    (best, chain) => (chain.length > best.length ? chain : best),
    [] as number[],
  );

// The ids of `after` whose order among the ids both hold is not the order
// of `before`: the fewest that, moved, give the new order.
const listMovedIds = (before: string[], after: string[]) => {
  const common = after.filter((id) => before.includes(id));
  const ranks = common.map((id) => before.indexOf(id));
  const chains = ranks.reduce<number[][]>(
    (found, rank, index) => [
      ...found,
      [...longest(found.filter((_, at) => ranks[at] < rank)), index],
    ],
    [],
  );
  const kept = new Set(longest(chains).map((index) => common[index]));
  return common.filter((id) => !kept.has(id));
};

// An empty line, such as one just added, has no wording to be named by.
const nameLine = (line: NoteLineDef) => describeNoteLine(line) || '…';

// A line's wording as typed, its inline guidance included, to be typed
// again; every value block is a gap.
const writeLineWording = (line: NoteLineDef) =>
  line.Blocks.map((block) =>
    block.Kind.case === 'Text' ? block.Kind.value.Value.trim() : '…',
  )
    .filter(Boolean)
    .join(' ');

// The hint a value block shows while empty; a wording and a context value
// have none.
const readHint = (block: NoteBlockDef) => {
  switch (block.Kind.case) {
    case 'Choice':
    case 'MultiChoice':
    case 'Teeth':
    case 'FreeText':
    case 'ListRef':
    case 'Date':
      return block.Kind.value.Label ?? '';
    default:
      return '';
  }
};

const readText = (block: NoteBlockDef) =>
  block.Kind.case === 'Text' ? block.Kind.value.Value.trim() : undefined;

const readOptions = (block: NoteBlockDef) =>
  block.Kind.case === 'Choice' || block.Kind.case === 'MultiChoice'
    ? block.Kind.value.Values
    : undefined;

// The block without what is named on its own: its wording, hint and values.
const withoutNamed = (block: NoteBlockDef) => ({
  ...block,
  Kind: {
    ...block.Kind,
    value: {
      ...block.Kind.value,
      Value: undefined,
      Label: undefined,
      Values: undefined,
    },
  },
});

const describeOptionChanges = (
  section: string,
  before: string[],
  after: string[],
): TemplateChangeSummary[] => {
  const removed = before.filter((value) => !after.includes(value));
  const added = after.filter((value) => !before.includes(value));
  const renamed =
    removed.length === added.length &&
    removed.every(
      (value, index) => before.indexOf(value) === after.indexOf(added[index]),
    );
  return [
    ...(renamed
      ? removed.map((from, index) => ({
          kind: 'renameOption' as const,
          section,
          from,
          to: added[index],
        }))
      : [
          ...removed.map((option) => ({
            kind: 'removeOption' as const,
            section,
            option,
          })),
          ...added.map((option) => ({
            kind: 'addOption' as const,
            section,
            option,
          })),
        ]),
    ...listMovedIds(before, after).map((option) => ({
      kind: 'moveOption' as const,
      section,
      option,
    })),
  ];
};

const describeBlockChange = (
  section: string,
  line: string,
  before: NoteBlockDef,
  after: NoteBlockDef,
): TemplateChangeSummary[] => {
  if (isSameStructure(before, after)) {
    return [];
  }
  const fromText = readText(before);
  const toText = readText(after);
  const fromHint = readHint(before).trim();
  const toHint = readHint(after).trim();
  const fromOptions = readOptions(before);
  const toOptions = readOptions(after);
  return [
    ...(fromText !== undefined && toText !== undefined && fromText !== toText
      ? [
          {
            kind: 'changeWording' as const,
            section,
            from: fromText,
            to: toText,
          },
        ]
      : []),
    ...(fromHint !== toHint
      ? [
          {
            kind: 'changeHint' as const,
            section,
            line,
            from: fromHint,
            to: toHint,
          },
        ]
      : []),
    ...(fromOptions && toOptions
      ? describeOptionChanges(section, fromOptions, toOptions)
      : []),
    ...(before.Kind.case !== after.Kind.case ||
    !isSameStructure(withoutNamed(before), withoutNamed(after))
      ? [{ kind: 'changeBlock' as const, section, line }]
      : []),
  ];
};

const describeLineChange = (
  section: string,
  before: NoteLineDef,
  after: NoteLineDef,
): TemplateChangeSummary[] => {
  const line = nameLine(before);
  const blocks = new Map(before.Blocks.map((block) => [block.ID, block]));
  const kept = new Set(after.Blocks.map((block) => block.ID));
  const moved = listMovedIds(
    before.Blocks.map((block) => block.ID),
    after.Blocks.map((block) => block.ID),
  );
  return [
    ...(isSameStructure(before.ShowWhen, after.ShowWhen)
      ? []
      : [{ kind: 'changeLinks' as const, section, line }]),
    ...before.Blocks.filter((block) => !kept.has(block.ID)).map(() => ({
      kind: 'removeBlock' as const,
      section,
      line,
    })),
    ...after.Blocks.flatMap((block) => {
      const previous = blocks.get(block.ID);
      return previous
        ? describeBlockChange(section, line, previous, block)
        : [{ kind: 'addBlock' as const, section, line }];
    }),
    ...(moved.length ? [{ kind: 'changeBlock' as const, section, line }] : []),
  ];
};

type PlacedLine = { line: NoteLineDef; section: DocumentSectionSpec };

const indexLines = (template: DocumentTemplate) =>
  new Map(
    template.sections.flatMap((section) =>
      section.Lines.map((line): [string, PlacedLine] => [
        line.ID,
        { line, section },
      ]),
    ),
  );

const describeSectionChanges = (
  source: DocumentTemplate,
  draft: DocumentTemplate,
): TemplateChangeSummary[] => {
  const before = new Map(
    source.sections.map((section) => [section.ID, section]),
  );
  const after = new Map(draft.sections.map((section) => [section.ID, section]));
  return [
    ...source.sections
      .filter((section) => !after.has(section.ID))
      .map((section) => ({
        kind: 'removeSection' as const,
        section: section.Title,
      })),
    ...draft.sections
      .filter((section) => !before.has(section.ID))
      .map((section) => ({
        kind: 'addSection' as const,
        section: section.Title,
      })),
    ...listMovedIds([...before.keys()], [...after.keys()]).map((id) => ({
      kind: 'moveSection' as const,
      section: after.get(id)?.Title ?? '',
    })),
    ...draft.sections.flatMap((section) => {
      const saved = before.get(section.ID);
      return saved
        ? describeSectionUpdate(saved, {
            Title: section.Title,
            Instructions: section.Instructions,
            Required: section.Required,
            AllowAITextRewrite: !!section.AllowAITextRewrite,
          })
        : [];
    }),
  ];
};

const describeLineChanges = (
  source: DocumentTemplate,
  draft: DocumentTemplate,
): TemplateChangeSummary[] => {
  const before = indexLines(source);
  const after = indexLines(draft);
  const movedInSections = draft.sections.flatMap((section) => {
    const saved = source.sections.find((item) => item.ID === section.ID);
    return saved
      ? listMovedIds(
          saved.Lines.map((line) => line.ID),
          section.Lines.map((line) => line.ID),
        )
      : [];
  });
  return [
    // A removed section names its lines with it.
    ...[...before.values()]
      .filter(
        ({ line, section }) =>
          !after.has(line.ID) &&
          draft.sections.some((item) => item.ID === section.ID),
      )
      .map(({ line, section }) => ({
        kind: 'removeLine' as const,
        section: section.Title,
        line: nameLine(line),
      })),
    ...[...after.values()].flatMap(({ line, section }) => {
      const saved = before.get(line.ID);
      if (!saved) {
        return [
          {
            kind: 'addLine' as const,
            section: section.Title,
            line: writeLineWording(line) || '…',
          },
        ];
      }
      const moved =
        saved.section.ID !== section.ID || movedInSections.includes(line.ID);
      return [
        ...(moved
          ? [
              {
                kind: 'moveLine' as const,
                section: section.Title,
                line: nameLine(saved.line),
              },
            ]
          : []),
        ...describeLineChange(section.Title, saved.line, line),
      ];
    }),
  ];
};

// The changes a block template's draft holds against its source, named the
// way they read, so they can be made again by hand on another version.
export const listTemplateDocumentChanges = (
  source: DocumentTemplate,
  draft: DocumentTemplate,
): TemplateChangeSummary[] =>
  [
    ...describeSectionChanges(source, draft),
    ...describeLineChanges(source, draft),
  ].filter(
    (change, index, all) =>
      all.findIndex((other) => isSameStructure(other, change)) === index,
  );
